import { useCallback } from "react";
import { useHistory, useLocation } from "react-router";

import {
    type HistoryActionKey,
    isActionMarkedInHistory,
    popActionFromHistory,
    pushActionToHistory,
} from "utilities/historyState/actionWithHistory.utility";

/**
 * Tracks a dismissable UI overlay (modal, drawer, settings panel) by pushing a
 * marker into `history.state` while it is open. The mobile back button pops
 * that entry, so `isOpen` flips to `false` and the consumer's overlay closes —
 * without leaving the page.
 *
 * Mirrors the `useEditMode` pattern (utilities/editMode/useEditMode.ts): history
 * state is the single source of truth. Consumers read `isOpen` from this hook
 * to drive visibility, and call `open()` / `close()` to transition.
 *
 * Multiple overlays on the same page must use distinct `actionKey`s — the
 * markers are namespaced under a shared parent key so they don't collide with
 * other consumers of history state.
 */
export interface IUseActionWithHistoryResult {
    isOpen: boolean;
    open: () => void;
    close: () => void;
}

export function useActionWithHistory(actionKey: HistoryActionKey): IUseActionWithHistoryResult {
    const history = useHistory();
    const location = useLocation();
    const isOpen = isActionMarkedInHistory(location, actionKey);

    const open = useCallback(() => {
        pushActionToHistory(location, history, actionKey);
    }, [location, history, actionKey]);

    const close = useCallback(() => {
        popActionFromHistory(history);
    }, [history]);

    return { isOpen, open, close };
}
